/* eslint-disable react/jsx-no-bind */
import React, { useCallback, useEffect, useRef } from 'react';
import logger from '../logger';
import { PortalMessages as M } from '../messages';
const ITEM_HEIGHT = 44;
const VISIBLE_COUNT = 5;
const SCROLL_SETTLE_MS = 90;
/**
 * 单列滚轮：中间高亮，选中项可带单位后缀。
 *
 * @param {IWheelColumnProps} props - Props.
 * @returns {React.ReactElement}
 */
export function WheelColumn({ onChange, options, selectedUnit, value }) {
    const listRef = useRef(null);
    const settleTimer = useRef();
    const syncing = useRef(false);
    const scrollToIndex = useCallback((index, smooth) => {
        const list = listRef.current;
        if (!list) {
            return;
        }
        const top = Math.max(0, index) * ITEM_HEIGHT;
        syncing.current = true;
        list.scrollTo({
            behavior: smooth ? 'smooth' : 'auto',
            top
        });
        window.setTimeout(() => {
            syncing.current = false;
        }, smooth ? 280 : 40);
    }, []);
    useEffect(() => {
        const index = Math.max(0, options.findIndex(option => option.value === value));
        const frame = window.requestAnimationFrame(() => {
            const list = listRef.current;
            if (!list) {
                return;
            }
            if (Math.round(list.scrollTop / ITEM_HEIGHT) !== index) {
                scrollToIndex(index, false);
            }
        });
        return () => window.cancelAnimationFrame(frame);
    }, [options, scrollToIndex, value]);
    const settle = () => {
        const list = listRef.current;
        if (!list || !options.length) {
            return;
        }
        const index = Math.min(options.length - 1, Math.max(0, Math.round(list.scrollTop / ITEM_HEIGHT)));
        const next = options[index].value;
        scrollToIndex(index, true);
        if (next !== value) {
            logger.debug(`[门户] ${new Date().toISOString()} 滚轮选中变更 | value=${next}`);
            onChange(next);
        }
    };
    const onScroll = () => {
        if (syncing.current) {
            return;
        }
        if (settleTimer.current) {
            window.clearTimeout(settleTimer.current);
        }
        settleTimer.current = window.setTimeout(settle, SCROLL_SETTLE_MS);
    };
    return (React.createElement("div", { className: 'portal-schedule-wheel-col', style: { height: ITEM_HEIGHT * VISIBLE_COUNT } },
        React.createElement("div", { className: 'portal-schedule-wheel-list', onScroll: onScroll, ref: listRef, style: {
                paddingBottom: ITEM_HEIGHT * 2,
                paddingTop: ITEM_HEIGHT * 2
            } }, options.map(option => (React.createElement("div", { className: option.value === value
                ? 'portal-schedule-wheel-item is-selected'
                : 'portal-schedule-wheel-item', key: option.value, style: { height: ITEM_HEIGHT } },
            React.createElement("span", null,
                option.label,
                option.value === value && selectedUnit ? selectedUnit : '')))))));
}
/**
 * 手机底部抽屉：关闭 / 标题 / 确认勾。
 *
 * @param {IPickerDrawerProps} props - Props.
 * @returns {React.ReactElement}
 */
export function SchedulePickerDrawer({ children, footer, onClose, onConfirm, title }) {
    return (React.createElement("div", { className: 'portal-schedule-picker-backdrop', onClick: onClose, role: 'presentation' },
        React.createElement("div", { className: 'portal-schedule-picker-sheet', onClick: event => event.stopPropagation() },
            React.createElement("div", { className: 'portal-schedule-picker-header' },
                React.createElement("button", { "aria-label": M.CLOSE, className: 'portal-schedule-picker-icon', onClick: onClose, type: 'button' },
                    React.createElement("svg", { fill: 'none', height: '22', viewBox: '0 0 24 24', width: '22' },
                        React.createElement("path", { d: 'M18 6 6 18M6 6l12 12', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2' }))),
                React.createElement("h3", null, title),
                React.createElement("button", { "aria-label": M.SCHEDULE_DONE, className: 'portal-schedule-picker-icon is-confirm', onClick: onConfirm, type: 'button' },
                    React.createElement("svg", { fill: 'none', height: '22', viewBox: '0 0 24 24', width: '22' },
                        React.createElement("path", { d: 'M5 12.5 10 17.5 19 7.5', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2.2' })))),
            React.createElement("div", { className: 'portal-schedule-wheel-wrap' },
                React.createElement("div", { className: 'portal-schedule-wheel-highlight' }),
                children),
            footer)));
}
